




























































import { Component } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import ManagementReportFilters, { ApplyPayload } from './ManagementReportFilters.vue';
import ExecutiveReports from './ExecutiveReports.vue';
import RevenueReports from './RevenueReports.vue';
import TempOpsReports from './TempOpsReports.vue';
import PermReports from './PermReports.vue';
import WorkerReports from './WorkerReports.vue';
import CompanyReports from './CompanyReports.vue';
import LocationReports from './LocationReports.vue';
import ActivityReports from './ActivityReports.vue';
import FinancialReports from './FinancialReports.vue';
import CampaignReports from './CampaignReports.vue';
import SpecOutReports from './SpecOutReports.vue';
import InactivityReports from './InactivityReports.vue';
import OpportunityReports from './OpportunityReports.vue';
import DataExportReports from './DataExportReports.vue';
import { ManagementReportsModule, ReportFilterParams } from '@/store/modules/managementReports';
import { InactivityReportModule } from '@/store/modules/inactivityReport';

type ReportKey =
  | 'executive' | 'revenue'
  | 'tempOps' | 'perm' | 'workers' | 'financial'
  | 'companies' | 'locations' | 'opportunities' | 'specOuts' | 'campaigns' | 'activity' | 'inactivity'
  | 'dataExport';

interface SubReport { key: ReportKey; label: string; }
interface ReportGroup { label: string; icon: string; subs: SubReport[]; }

@Component({
  name: 'ManagementReports',
  components: {
    ManagementReportFilters,
    ExecutiveReports,
    RevenueReports,
    TempOpsReports,
    PermReports,
    WorkerReports,
    CompanyReports,
    LocationReports,
    ActivityReports,
    SpecOutReports,
    FinancialReports,
    CampaignReports,
    InactivityReports,
    OpportunityReports,
    DataExportReports,
  }
})
export default class ManagementReports extends GiganautVue {

  // Hold the loading overlay visible for at least this long so a fast response
  // doesn't produce a sub-perceptual flash. Long enough to read "Loading
  // reports…" rather than seeing a flicker.
  static LOADING_MIN_MS = 1000;

  // Computed so Workers / Companies / Locations sub-tab labels pick up the
  // agency's customised terminology from AgencyLabelDto.
  get groups(): ReportGroup[] {
    const workersLabel = this.Labels.workers || 'Workers';
    const companiesLabel = this.Labels.companies || 'Companies';
    const locationsLabel = this.Labels.locations || 'Locations';
    return [
      { label: 'EXECUTIVE', icon: 'mdi-finance', subs: [
        { key: 'executive', label: 'Summary' },
        { key: 'revenue', label: 'Revenue' },
      ]},
      { label: 'OPERATIONS', icon: 'mdi-cog-outline', subs: [
        { key: 'tempOps', label: 'Temp' },
        { key: 'perm', label: 'Perm' },
        { key: 'workers', label: workersLabel },
        { key: 'financial', label: 'Financial' },
      ]},
      { label: 'CRM', icon: 'mdi-account-multiple-outline', subs: [
        { key: 'companies', label: companiesLabel },
        { key: 'locations', label: locationsLabel },
        { key: 'opportunities', label: 'Opportunities' },
        { key: 'specOuts', label: 'Spec Outs' },
        { key: 'campaigns', label: 'Campaigns' },
        { key: 'activity', label: 'Activity' },
        { key: 'inactivity', label: 'Inactivity' },
      ]},
      { label: 'DATA EXPORT', icon: 'mdi-database-export-outline', subs: [
        { key: 'dataExport', label: 'Data Export' },
      ]},
    ];
  }

  topTab = 0;
  subTab = 0;
  // One remembered sub-tab index per top-level tab, so switching back to a
  // category restores the user's last sub-selection.
  subTabByTop: number[] = [0, 0, 0, 0];
  // Standard-mode filter params, retained across sub-tab switches so we can
  // re-fire load* actions without re-asking the user for filters.
  standardFilters: ReportFilterParams | null = null;
  loading = false;
  errorMessage = '';
  // Track loaded report keys against the current standardFilters / inactivity
  // request — cleared when filters change so subsequent tab switches re-fetch.
  // Object keyed by report key (not a Set) because Vue 2 doesn't observe Set
  // mutations, which means computeds that read .has() wouldn't re-evaluate.
  loadedKeys: { [k: string]: boolean } = {};

  get currentGroup(): ReportGroup { return this.groups[this.topTab]; }
  get activeKey(): ReportKey {
    const sub = this.currentGroup.subs[this.subTab];
    return sub ? sub.key : this.currentGroup.subs[0].key;
  }

  get filterMode(): 'standard' | 'inactivity' {
    return this.activeKey === 'inactivity' ? 'inactivity' : 'standard';
  }

  // Data Export uses its own date pickers per export and doesn't share the
  // sidebar filter, so we hide the filter panel for that tab.
  get showFilters(): boolean {
    return this.activeKey !== 'dataExport';
  }

  // True from the moment the user lands on an unloaded report through to the
  // API returning AND the minimum-visible delay elapsing. Standard reports
  // need filters before they can load; inactivity tracks its own response.
  get showLoadingOverlay(): boolean {
    if (this.loading) return true;
    if (this.activeKey === 'inactivity' || this.activeKey === 'dataExport') return false;
    if (this.standardFilters && !this.loadedKeys[this.activeKey]) return true;
    return false;
  }

  // v-tabs @change fires whenever the user clicks a tab — driving the loader
  // from explicit events rather than watching a computed turned out to be
  // more reliable, so we hang the load triggers off these handlers.
  onTopTabChanged(newTop: number) {
    // Restore this top tab's remembered sub-selection.
    const remembered = this.subTabByTop[newTop] || 0;
    if (this.subTab !== remembered) {
      this.subTab = remembered;
    }
    this.$nextTick(() => this.maybeLoadActive());
  }

  onSubTabChanged(newSub: number) {
    // Persist the user's pick so switching back to this top tab restores it.
    this.$set(this.subTabByTop, this.topTab, newSub);
    this.$nextTick(() => this.maybeLoadActive());
  }

  onFiltersApplied(payload: ApplyPayload) {
    this.errorMessage = '';
    this.loadedKeys = {};
    if (payload.mode === 'inactivity') {
      InactivityReportModule.patchRequest({
        lookbackDays: payload.params.lookbackDays,
        branchId: payload.params.branchId,
        deskId: payload.params.deskId,
        ownerId: payload.params.ownerId,
        includeNever: payload.params.includeNever,
      });
      this.runInactivity();
      return;
    }
    this.standardFilters = payload.params;
    this.loadActive();
  }

  // Loads the active report's data if filters are set and it hasn't been
  // loaded yet — called on tab switches.
  maybeLoadActive() {
    if (this.activeKey === 'inactivity') {
      // Inactivity is filter-gated by the user clicking Apply; don't auto-fire.
      return;
    }
    if (this.activeKey === 'dataExport') return;
    if (!this.standardFilters) return;
    if (this.loadedKeys[this.activeKey]) return;
    this.loadActive();
  }

  async loadActive() {
    if (!this.standardFilters) return;
    if (this.activeKey === 'dataExport' || this.activeKey === 'inactivity') return;
    this.loading = true;
    this.errorMessage = '';
    const minVisible = new Promise(r => setTimeout(r, (this.$options as any).LOADING_MIN_MS || 1000));
    const f = this.standardFilters;
    const key = this.activeKey;
    try {
      switch (key) {
        case 'executive':
          await Promise.all([
            ManagementReportsModule.loadExecutiveSummary(f),
            ManagementReportsModule.loadLeagueTable(f),
          ]);
          break;
        case 'revenue':
          await Promise.all([
            ManagementReportsModule.loadRevenueOverview(f),
            ManagementReportsModule.loadRevenueByCompany(f),
            ManagementReportsModule.loadRevenueByPosition(f),
            ManagementReportsModule.loadRevenueByConsultant(f),
            ManagementReportsModule.loadChargeRateAnalysis(f),
          ]);
          break;
        case 'tempOps':
          await Promise.all([
            ManagementReportsModule.loadBookingVolume(f),
            ManagementReportsModule.loadFillRate(f),
            ManagementReportsModule.loadCancellations(f),
            ManagementReportsModule.loadWorkerDeployment(f),
            ManagementReportsModule.loadTimesheetApproval(f),
          ]);
          break;
        case 'perm':
          await Promise.all([
            ManagementReportsModule.loadPipelineSnapshot(f),
            ManagementReportsModule.loadConversionFunnel(f),
            ManagementReportsModule.loadPlacements(f),
          ]);
          break;
        case 'workers':
          await Promise.all([
            ManagementReportsModule.loadWorkerRegistration(f),
            ManagementReportsModule.loadWorkerUtilisation(f),
            ManagementReportsModule.loadWorkerCompliance(f),
            ManagementReportsModule.loadCvsParsedByUser(f),
          ]);
          break;
        case 'financial':
          await ManagementReportsModule.loadPayrollSummary(f);
          break;
        case 'companies':
          await Promise.all([
            ManagementReportsModule.loadCompanyOverview(f),
            ManagementReportsModule.loadCompanyDormancy(f),
            ManagementReportsModule.loadCompanyProfitability(f),
          ]);
          break;
        case 'locations':
          await Promise.all([
            ManagementReportsModule.loadLocationOverview(f),
            ManagementReportsModule.loadLocationDormancy(f),
            ManagementReportsModule.loadLocationProfitability(f),
          ]);
          break;
        case 'opportunities':
          await Promise.all([
            ManagementReportsModule.loadOpportunityOverview(f),
            ManagementReportsModule.loadOpportunityDormancy(f),
          ]);
          break;
        case 'specOuts':
          await ManagementReportsModule.loadSpecOutVolume(f);
          break;
        case 'campaigns':
          await ManagementReportsModule.loadCampaignPerformance(f);
          break;
        case 'activity':
          await Promise.all([
            ManagementReportsModule.loadActivityLeaderboard(f),
            ManagementReportsModule.loadActivityMix(f),
            ManagementReportsModule.loadCallEffectiveness(f),
            ManagementReportsModule.loadTaskManagement(f),
          ]);
          break;
      }
      this.$set(this.loadedKeys, key, true);
    } catch (e: any) {
      this.errorMessage = e.message || 'Failed to load reports';
      console.error('[ManagementReports] loadActive error:', e);
      // Mark loaded even on error so the overlay clears and the user can
      // read the error alert instead of staring at an infinite spinner.
      this.$set(this.loadedKeys, key, true);
    } finally {
      await minVisible;
      this.loading = false;
    }
  }

  async runInactivity() {
    this.loading = true;
    try {
      await InactivityReportModule.run();
      this.$set(this.loadedKeys, 'inactivity', true);
    } catch (e: any) {
      this.errorMessage = e.message || 'Failed to load inactivity report';
    } finally {
      this.loading = false;
    }
  }

  beforeDestroy() {
    ManagementReportsModule.cleanState();
  }
}
